import { createTheme, PaletteMode, Theme } from "@mui/material/styles";

/**
 * Brand accent — a cyan→violet sweep used for focus rings, gradient hairline
 * borders and glow. Shared across both modes so the identity stays consistent.
 */
export const ACCENT_FROM = "#0e86d4"; // brand cyan-blue
export const ACCENT_VIA = "#5b8def"; // periwinkle
export const ACCENT_TO = "#8b5cf6"; // violet
export const ACCENT_GRADIENT = `linear-gradient(135deg, ${ACCENT_FROM} 0%, ${ACCENT_VIA} 50%, ${ACCENT_TO} 100%)`;

/**
 * Per-mode design tokens. Centralising these keeps the component overrides
 * (Paper glass, button gradients, etc.) free of `mode === "dark"` branching.
 */
const tokens = (mode: PaletteMode) => {
  const isDark = mode === "dark";
  return {
    // Translucent "glass" surface used by Paper / Menu.
    glass: isDark ? "rgba(26, 33, 49, 0.66)" : "rgba(255, 255, 255, 0.66)",
    glassStrong: isDark
      ? "rgba(30, 38, 56, 0.85)"
      : "rgba(255, 255, 255, 0.82)",
    glassBorder: isDark
      ? "rgba(255, 255, 255, 0.07)"
      : "rgba(255, 255, 255, 0.55)",
    // A faint gradient hairline laid over the glass edge for a premium rim.
    rimGradient: isDark
      ? "linear-gradient(135deg, rgba(91,141,239,0.35), rgba(139,92,246,0.12) 45%, rgba(255,255,255,0.04))"
      : "linear-gradient(135deg, rgba(14,134,212,0.30), rgba(139,92,246,0.10) 45%, rgba(255,255,255,0.6))",
    // Inputs / outlined buttons.
    inputBg: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.6)",
    inputBgHover: isDark
      ? "rgba(255, 255, 255, 0.07)"
      : "rgba(255, 255, 255, 0.8)",
    inputBgFocus: isDark
      ? "rgba(255, 255, 255, 0.09)"
      : "rgba(255, 255, 255, 0.9)",
    outlinedText: isDark ? "#e6ecf5" : "#1a2332",
    // Soft, layered, slightly cool-tinted shadows.
    shadow: isDark
      ? "0 1px 2px rgba(0,0,0,0.5), 0 12px 40px -8px rgba(0,0,0,0.6)"
      : "0 1px 2px rgba(16,42,80,0.04), 0 12px 40px -12px rgba(16,42,80,0.18)",
    shadowHover: isDark
      ? "0 2px 6px rgba(0,0,0,0.55), 0 24px 60px -10px rgba(91,141,239,0.30)"
      : "0 4px 12px rgba(16,42,80,0.08), 0 28px 64px -16px rgba(14,134,212,0.30)",
    menuShadow: isDark
      ? "0 12px 48px -8px rgba(0,0,0,0.65)"
      : "0 12px 48px -12px rgba(16,42,80,0.28)",
    appBarShadow: isDark
      ? "0 1px 24px rgba(0,0,0,0.5)"
      : "0 1px 24px rgba(16,42,80,0.12)",
  };
};

/**
 * Builds the TeamRay MUI theme for the given palette mode. Light and dark
 * share the brand primary/secondary and all shape/typography/component
 * overrides — only the palette and a handful of surface tokens differ.
 */
export const createAppTheme = (mode: PaletteMode): Theme => {
  const isDark = mode === "dark";
  const t = tokens(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: "#0e86d4",
        light: "#3da2e2",
        dark: "#0a6baa",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#8b5cf6",
        light: "#a78bfa",
        dark: "#7c3aed",
        contrastText: "#ffffff",
      },
      background: isDark
        ? { default: "#0a0e16", paper: t.glass }
        : { default: "#eef3f9", paper: t.glass },
      text: isDark
        ? { primary: "#e8edf6", secondary: "#9aa9bd" }
        : { primary: "#16233a", secondary: "#5a6d80" },
      error: {
        main: "#f0506e",
        light: isDark ? "rgba(240, 80, 110, 0.16)" : "#fff5f7",
      },
      success: {
        main: "#22c55e",
        light: isDark ? "rgba(34, 197, 94, 0.16)" : "#f0fdf4",
      },
      divider: isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(22, 35, 58, 0.08)",
    },
    typography: {
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
      h4: {
        fontWeight: 750,
        letterSpacing: "-0.03em",
      },
      h5: {
        fontWeight: 720,
        letterSpacing: "-0.025em",
      },
      h6: {
        fontWeight: 650,
        letterSpacing: "-0.02em",
      },
      body1: {
        lineHeight: 1.6,
      },
      body2: {
        lineHeight: 1.5,
      },
      button: {
        textTransform: "none" as const,
        fontWeight: 600,
        letterSpacing: "0.005em",
      },
    },
    shape: {
      borderRadius: 18,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            padding: "10px 22px",
            fontSize: "0.938rem",
            boxShadow: "none",
            transition: "all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
            "&:hover": {
              boxShadow: "none",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
              transition: "all 0.1s ease",
            },
          },
          contained: ({ theme }) => ({
            background: ACCENT_GRADIENT,
            backgroundSize: "180% 180%",
            backgroundPosition: "0% 50%",
            boxShadow: "0 4px 16px -4px rgba(14, 134, 212, 0.45)",
            transition:
              "background-position 0.5s ease, box-shadow 0.28s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
            "&:hover": {
              backgroundPosition: "100% 50%",
              boxShadow: "0 8px 28px -6px rgba(91, 141, 239, 0.55)",
            },
            "&.Mui-disabled": {
              color: theme.palette.action.disabled,
              background: theme.palette.action.disabledBackground,
              backgroundImage: "none",
              boxShadow: "none",
            },
          }),
          outlined: {
            borderColor: isDark
              ? "rgba(91, 141, 239, 0.3)"
              : "rgba(14, 134, 212, 0.25)",
            color: t.outlinedText,
            backdropFilter: "blur(8px)",
            backgroundColor: t.inputBg,
            "&:hover": {
              borderColor: ACCENT_VIA,
              backgroundColor: isDark
                ? "rgba(91, 141, 239, 0.12)"
                : "rgba(14, 134, 212, 0.08)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            position: "relative",
            borderRadius: 22,
            backgroundColor: t.glass,
            backgroundImage: "none",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            border: "1px solid transparent",
            boxShadow: t.shadow,
            transition:
              "box-shadow 0.32s cubic-bezier(0.4, 0, 0.2, 1), transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.32s ease",
            // Gradient hairline rim drawn via a masked ::before so it sits on
            // the very edge without affecting layout.
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              padding: "1px",
              background: t.rimGradient,
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              pointerEvents: "none",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            "&:hover": {
              boxShadow: t.shadowHover,
              transform: "translateY(-2px)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 14,
              backgroundColor: t.inputBg,
              backdropFilter: "blur(8px)",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: t.inputBgHover,
              },
              "&.Mui-focused": {
                backgroundColor: t.inputBgFocus,
                boxShadow: `0 0 0 3px ${
                  isDark ? "rgba(91,141,239,0.25)" : "rgba(14,134,212,0.14)"
                }`,
              },
              "& fieldset": {
                borderColor: isDark
                  ? "rgba(91, 141, 239, 0.18)"
                  : "rgba(14, 134, 212, 0.15)",
                transition: "border-color 0.25s ease",
              },
              "&:hover fieldset": {
                borderColor: isDark
                  ? "rgba(91, 141, 239, 0.4)"
                  : "rgba(14, 134, 212, 0.35)",
              },
              "&.Mui-focused fieldset": {
                borderColor: ACCENT_VIA,
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: t.appBarShadow,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardError: {
            borderRadius: 14,
            backgroundColor: "rgba(240, 80, 110, 0.08)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(240, 80, 110, 0.18)",
          },
          standardSuccess: {
            borderRadius: 14,
            backgroundColor: "rgba(34, 197, 94, 0.08)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(34, 197, 94, 0.18)",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            border: `2px solid ${t.glassBorder}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
            backgroundColor: t.glassStrong,
            backgroundImage: "none",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            boxShadow: t.menuShadow,
            border: `1px solid ${t.glassBorder}`,
            marginTop: 8,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: "2px 8px",
            padding: "8px 12px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(91, 141, 239, 0.14)"
                : "rgba(14, 134, 212, 0.1)",
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark
              ? "rgba(255, 255, 255, 0.07)"
              : "rgba(22, 35, 58, 0.08)",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 10,
            backgroundColor: isDark
              ? "rgba(20, 26, 38, 0.95)"
              : "rgba(22, 35, 58, 0.92)",
            backdropFilter: "blur(8px)",
            fontSize: "0.75rem",
            padding: "6px 10px",
          },
          arrow: {
            color: isDark ? "rgba(20, 26, 38, 0.95)" : "rgba(22, 35, 58, 0.92)",
          },
        },
      },
    },
  });
};

// Default light theme, kept for any non-provider import sites.
const theme = createAppTheme("light");

export default theme;
